import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeaderContent,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/layouts'
import type {
  CompradorPessoaFisicaStepSchemaInput,
  CompradorPessoaFisicaStepSchemaOutput,
  CompradorVinculoStepSchemaInput,
  CompradorVinculoStepSchemaOutput,
} from '@/features/proposta/schemas/compradores/comprador-pf.schema'
import type {
  CompradorPessoaJuridicaSchemaInput,
  CompradorPessoaJuridicaSchemaOutput,
} from '@/features/proposta/schemas/compradores/comprador-pj.schema'
import {
  ESTADO_CIVIL_OPTIONS,
  REGIME_CASAMENTO_OPTIONS,
  UNIAO_ESTAVEL_OPTIONS,
} from '@/features/proposta/utils/constants'
import { Render } from '@/shared/components/render-if'
import { Button, RadioGroupField } from '@/shared/components/ui'
import { ComboboxField } from '@/shared/components/ui/combobox'
import { useGuardedHandler } from '@/shared/hooks/use-guarded-handlers'
import { EstadoCivilEnum } from '@/shared/interfaces/cliente.enums'
import { checkOnlineGuard } from '@/shared/utils/guards/check-online-guard'

type TContatosCompradorSchemaOutput =
  | CompradorPessoaFisicaStepSchemaOutput
  | CompradorPessoaJuridicaSchemaOutput
  | CompradorVinculoStepSchemaOutput

type TContatosCompradorSchemaInput =
  | CompradorPessoaFisicaStepSchemaInput
  | CompradorPessoaJuridicaSchemaInput
  | CompradorVinculoStepSchemaInput

type TEstadoCivilComprador = {
  fieldPath: 'maePai.estadoCivil' | 'responsavel.estadoCivil' | 'estadoCivil'
  onBack: () => void
  onNext: () => void
  onFinish: (value: TContatosCompradorSchemaOutput) => Promise<void>
  isLast: boolean
}

export const EstadoCivilVinculo = ({ onBack, isLast, onNext, onFinish, fieldPath }: TEstadoCivilComprador) => {
  const prefix = 'proposta:cadastro_compradores.steps.estado_civil'
  const {
    control,
    watch,
    setValue,
    trigger,
    handleSubmit,
    formState: { isSubmitting },
  } = useFormContext<TContatosCompradorSchemaInput, unknown, TContatosCompradorSchemaOutput>()
  const estadoCivil = watch(`${fieldPath}.status`)

  const handleConcluirCadastro = useGuardedHandler(
    [checkOnlineGuard()],
    async (formOutput: TContatosCompradorSchemaOutput) => {
      await onFinish(formOutput)
    },
  )

  const handleNext = async () => {
    if (isLast) {
      handleSubmit(handleConcluirCadastro)()
      return
    }
    const isValid = await trigger(fieldPath)
    if (!isValid) return
    onNext()
  }

  const titulo = () => {
    if (fieldPath === 'responsavel.estadoCivil') {
      return t(`${prefix}.responsavel_titulo`, {
        responsavel: watch('responsavel.tipo') == 'Representante' ? 'Representante Legal' : 'Procurador',
      })
    }
    if (fieldPath === 'estadoCivil') return t(`${prefix}.procurador_titulo`)
    return t(`${prefix}.mae_pai_titulo`)
  }

  return (
    <StepPropostaRoot>
      <StepPropostaHeaderContent>
        <StepPropostaHeaderTitle>{titulo()}</StepPropostaHeaderTitle>
      </StepPropostaHeaderContent>
      <StepPropostaContent hasBorder>
        <div className="w-full">
          <ComboboxField
            className="w-full sm:w-90"
            control={control}
            placeholder={t(`${prefix}.placeholder.selecione`)}
            label={t(`${prefix}.label.estado_civil`)}
            name={`${fieldPath}.status`}
            valueProperty="value"
            labelProperty="label"
            hideClearButton
            showSearch={false}
            options={ESTADO_CIVIL_OPTIONS}
            onSelect={() => setValue('maePai.estadoCivil.uniaoEstavel', null)}
          />
          <Render.If condition={estadoCivil && estadoCivil !== EstadoCivilEnum.CivilCasado}>
            {() => (
              <RadioGroupField
                name={`${fieldPath}.uniaoEstavel`}
                control={control}
                groupLabel={t(`${prefix}.label.uniao_estavel`)}
                options={UNIAO_ESTAVEL_OPTIONS}
              />
            )}
          </Render.If>
          <Render.If condition={estadoCivil && estadoCivil === EstadoCivilEnum.CivilCasado}>
            {() => (
              <ComboboxField
                className="w-full sm:w-90"
                control={control}
                placeholder={t(`${prefix}.placeholder.selecione`)}
                label={t(`${prefix}.label.regime_casamento`)}
                name={`${fieldPath}.regimeCasamento`}
                valueProperty="value"
                labelProperty="label"
                hideClearButton
                showSearch={false}
                options={REGIME_CASAMENTO_OPTIONS}
              />
            )}
          </Render.If>
        </div>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button variant="outline" onClick={onBack} className="font-nunito h-12 w-full md:w-auto">
          {t('proposta:cadastro_compradores.steps.voltar')}
        </Button>
        <Button
          className="font-nunito h-12 w-full md:w-27"
          onClick={handleNext}
          disabled={isSubmitting}
          isLoading={isSubmitting}
          showLoadingText={false}
        >
          {t(isLast ? 'proposta:cadastro_compradores.steps.concluir' : 'proposta:cadastro_compradores.steps.proximo')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
